import * as React from 'react';
import ComposeEmailFunction from '../ComposeEmailFunction';
import fetchBrandList from '../services/fetchBrandList';
import initializeLivePersonaCard from '../actions/initializeLivePersonaCard';
import personaControlStore from '../store/Store';
import { logUsage } from 'owa-analytics';
import { lokiClientCorrelationId } from 'owa-people-loki';
import { observer } from 'mobx-react-lite';
import { getPersonaInitialsColor, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { calculatePersonaType, LivePersonaCardPersonaTypes } from '../utils/calculatePersonaType';
import { usePersonaRingBehavior } from './PersonaRingBehavior';

export interface PersonaCardBehaviorInputProps {
    emailAddress: string;
    name: string;
    olsPersonaId?: string;
    personaType?: string;
    composeEmailOverride?: ComposeEmailFunction;
    disableHover?: boolean;
    ariaLabel?: string;
    className?: string;
    mailBoxType?: string;
    isUnauthenticatedSender?: boolean;
    locationToOpen?: string;
    clientScenario?: string;
    focusReturnElement?:
        | HTMLElement
        | React.RefObject<HTMLElement>
        | React.MutableRefObject<HTMLElement>;
    aadObjectId?: string;
    displayPersonaHighlightRing?: boolean;
    size?: PersonaSize;
    /**
     * Mounts the hover target around the child without needing a wrapper span.
     *
     * If this is set, the rendered ReactElement of the child _must_ be able to
     * accept a `className` prop, since LPC clones & appends a special
     * hover-target class name . (e.g. it  cannot be a fragment)
     */
    mountWithoutWrapperSpan?: boolean;
}

export interface PersonaCardBehaviorProps extends PersonaCardBehaviorInputProps {
    tabIndex?: number;
}

interface PersonaCardBehaviorInternalProps extends PersonaCardBehaviorProps {
    children: React.ReactNode;
}

interface PersonaInfo {
    personaType: string;
    isBrand: boolean;
    isTier3Brand: boolean;
}

const LivePersonaCardBehavior = observer(function LivePersonaCardBehavior({
    composeEmailOverride,
    isUnauthenticatedSender,
    mailBoxType,
    personaType,
    emailAddress,
    clientScenario,
    locationToOpen,
    focusReturnElement,
    tabIndex,
    olsPersonaId,
    name,
    disableHover,
    ariaLabel,
    className,
    children,
    size,
    aadObjectId,
    displayPersonaHighlightRing,
    mountWithoutWrapperSpan,
}: PersonaCardBehaviorInternalProps) {
    const [hasPersonaInsightRing, setHasPersonaInsightRing] = React.useState(false);

    React.useEffect(() => {
        initializeLivePersonaCard(composeEmailOverride);
        fetchBrandList();
    }, []);

    const calculatedPersonaType = React.useMemo(
        () => calculatePersonaType(isUnauthenticatedSender, mailBoxType, personaType, emailAddress),
        [
            isUnauthenticatedSender,
            mailBoxType,
            personaType,
            emailAddress,
            personaControlStore.brandListLoadState,
        ]
    );

    const onCardOpen = React.useCallback(
        () => logInfoOnCardOpen(calculatedPersonaType, clientScenario, locationToOpen),
        [calculatedPersonaType, clientScenario, locationToOpen]
    );

    const onCardClose = React.useCallback(() => {
        if (focusReturnElement instanceof HTMLElement) {
            focusReturnElement.focus();
        } else if (focusReturnElement && focusReturnElement.current) {
            focusReturnElement.current.focus();
        }
    }, [focusReturnElement]);

    const onPersonaInsightRingStateChange = React.useCallback(
        (hasPersonaInsightRing: boolean) => {
            setHasPersonaInsightRing(hasPersonaInsightRing);
        },
        [setHasPersonaInsightRing]
    );

    const personaCoinColor = getPersonaInitialsColor({ text: name });

    const PersonaRingBehavior = usePersonaRingBehavior({
        displayPersonaHighlightRing,
        personaSize: size || PersonaSize.size40,
        identifiers: {
            OlsPersonaId: olsPersonaId,
            Smtp: emailAddress,
            PersonaType: calculatedPersonaType.personaType,
        },
        onPersonaInsightRingStateChange,
    });

    if (personaControlStore.isLivePersonaCardInitialized) {
        let LivePersonaCardHoverTargetV2 = (window as any).LivePersonaCardHoverTargetV2;

        const wrappedChildren = displayPersonaHighlightRing ? (
            <PersonaRingBehavior>{children}</PersonaRingBehavior>
        ) : (
            children
        );

        return (
            <LivePersonaCardHoverTargetV2
                tabIndex={tabIndex}
                cardParameters={{
                    externalAppSessionCorrelationId: lokiClientCorrelationId,
                    personaInfo: {
                        identifiers: {
                            Smtp: emailAddress,
                            PersonaType: calculatedPersonaType.personaType,
                            OlsPersonaId: olsPersonaId,
                            AadObjectId: aadObjectId,
                        },
                        isUnauthenticatedSender: isUnauthenticatedSender,
                        displayName: name,
                        isTier3Brand: calculatedPersonaType.isTier3Brand,
                        personaCoinColor: personaCoinColor,
                    },
                    behavior: {
                        disableHover: disableHover,
                        onCardOpen: onCardOpen,
                        onCardClose: onCardClose,
                        locationToOpen: locationToOpen,
                        clientScenario: clientScenario,
                    },
                    ariaLabel: ariaLabel,
                    hasPersonaInsightRing,
                }}>
                {mountWithoutWrapperSpan ? (
                    wrappedChildren
                ) : (
                    <span className={className} aria-label={ariaLabel}>
                        {wrappedChildren}
                    </span>
                )}
            </LivePersonaCardHoverTargetV2>
        );
    } else {
        return <>{children}</>;
    }
});

function logInfoOnCardOpen(
    personaInfo: PersonaInfo,
    clientScenario?: string,
    locationToOpen?: string
) {
    if (personaInfo.isBrand) {
        logUsage(
            personaInfo.personaType === LivePersonaCardPersonaTypes.Brand
                ? 'BrandCardDisplayed'
                : 'BrandDetectedButNotDisplayed'
        );
    }

    const scenarioToPrint = clientScenario || 'No Client Scenario';
    const locationToPrint = locationToOpen || 'LPC';
    logUsage('LPCRenderedFromHoverTarget', {
        clientScenario: scenarioToPrint,
        locationToOpen: locationToPrint,
    });
}

export function useLivePersonaCard(props: PersonaCardBehaviorProps): React.ComponentType<{}> {
    return observer(function PersonaCardBehavior({ children }: { children?: React.ReactNode }) {
        if (personaControlStore.isPersonaCardDisabled) {
            return <>{children}</>;
        } else {
            return <LivePersonaCardBehavior {...props}>{children}</LivePersonaCardBehavior>;
        }
    });
}
